import { useState, useCallback, useMemo } from 'react';
import { Head, Link } from '@inertiajs/react';
import { MainLayout } from '../../layouts/MainLayout';
import { PageHeader } from '../../components/PageHeader';
import { PackCard } from '../../components/packs/PackCard';
import { EmptyState } from '../../components/packs/EmptyState';
import { Toast } from '../../components/packs/Toast';
import { useCartPacks } from '../../hooks/useCartPacks';
import { useAddPack } from '../../hooks/useAddPack';
import { route } from '../../utils/route';
import type { ProductPack } from '../../types/packs';

const SUBTITLES = [
  'Combinaciones pensadas para simplificar tu compra.',
  'Ideas listas para que no tengas que elegir uno por uno.',
  'Selecciones armadas para facilitarte la vida.',
  'Productos que combinan bien, listos para agregar.',
];

interface PacksProps {
  packs: ProductPack[];
  appUrl: string;
}

export default function Packs({ packs, appUrl }: PacksProps) {
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const subtitle = useMemo(() => SUBTITLES[Math.floor(Math.random() * SUBTITLES.length)], []);

  const { isPackInCart, addPackTracking, removePackTracking, refreshCart } = useCartPacks();

  const showSuccessToast = useCallback((message: string) => {
    setToastMessage(message);
    setShowToast(true);
  }, []);

  const { addPack, removePack, loadingPackId } = useAddPack({
    onSuccess: (pack, itemKeys) => {
      addPackTracking(pack, itemKeys);
      showSuccessToast(`Pack "${pack.title}" agregado al carrito`);
    },
    onRemoveSuccess: (packId) => {
      removePackTracking(packId);
      showSuccessToast('Pack removido del carrito');
    },
    refreshCart,
  });

  const handleAdd = useCallback(async (pack: ProductPack) => {
    try { await addPack(pack); } catch {}
  }, [addPack]);

  const handleRemove = useCallback(async (pack: ProductPack) => {
    try { await removePack(pack); } catch {}
  }, [removePack]);

  const pageDescription = 'Descubrí nuestros packs de productos seleccionados. Combos armados de frutas, verduras y más. Agregá todo al carrito con un click.';
  const pageUrl = `${appUrl}${route('packs.index')}`;

  const listSchema = useMemo(() => JSON.stringify({
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Packs de Productos - Abasto Virtual",
    "description": pageDescription,
    "numberOfItems": packs.length,
    "itemListElement": packs.map((pack, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "url": `${appUrl}${route('packs.show', { id: pack.slug || pack.id })}`,
      "name": pack.title,
    })),
  }), [packs, appUrl, pageDescription]);

  return (
    <MainLayout>
      <Head>
        <title>Packs de Productos - Abasto Virtual</title>
        <meta name="description" content={pageDescription} />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Packs de Productos - Abasto Virtual" />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:locale" content="es_AR" />
        <meta property="og:site_name" content="Abasto Virtual" />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content="Packs de Productos - Abasto Virtual" />
        <meta property="twitter:description" content={pageDescription} />
        <script type="application/ld+json">{listSchema}</script>
        <link rel="alternate" type="text/plain" href="/packs.md" title="LLM Context" />
      </Head>

      <div className="mx-auto max-w-6xl px-4 py-6">
        {packs.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            <PageHeader
              title="Packs de Productos"
              subtitle={`${subtitle} Agregá todo a tu carrito con un solo click.`}
            />

            {/* Disclaimers */}
            <div className="mb-6 flex flex-col gap-2">
              <p className="m-0 flex items-start gap-1.5 text-[13px] leading-snug text-gray-400">
                <i className="fa fa-info-circle mt-0.5 flex-shrink-0 text-xs" />
                Las imágenes son ilustrativas y generadas con IA. Los productos y el packaging pueden variar.
              </p>
              <p className="m-0 flex items-start gap-1.5 text-[13px] leading-snug text-gray-400">
                <i className="fa fa-shopping-cart mt-0.5 flex-shrink-0 text-xs" />
                Una vez agregado, podés modificar cantidades o eliminar productos del carrito de forma individual.
              </p>
            </div>

            <Link
              href="/bolson-de-verduras"
              className="mb-6 block rounded-2xl border border-green-200 bg-green-50 px-5 py-4 no-underline hover:bg-green-100"
            >
              <span className="block text-base font-semibold text-green-900">Bolsón de verduras a domicilio</span>
              <span className="mt-1 block text-sm text-green-800">Qué trae, cuánto dura y cuánto sale puesto en tu casa.</span>
            </Link>

            {/* Grid */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 lg:grid-cols-3 lg:gap-6">
              {packs.map((pack) => (
                <PackCard
                  key={pack.id}
                  pack={pack}
                  isInCart={isPackInCart(pack)}
                  isLoading={loadingPackId === pack.id}
                  onAdd={() => handleAdd(pack)}
                  onRemove={() => handleRemove(pack)}
                />
              ))}
            </div>
          </>
        )}

        <p className="mt-6 text-sm text-gray-600">
          <Link href="/por-que-elegirnos" className="font-semibold text-green-700 underline hover:text-green-800">
            Súper o verdulería online: por qué elegirnos
          </Link>
        </p>

        {showToast && (
          <Toast message={toastMessage} onClose={() => setShowToast(false)} />
        )}
      </div>
    </MainLayout>
  );
}
